import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
// import { loadStripeTerminal } from "@stripe/terminal-js";
import { calculations } from '@blocktickets/shared-utils'

import isEqual from 'lodash/isEqual'
import moment from 'moment'

import { useModal } from '../../ModalProvider/ModalProvider'

import AuthService from '../../../utilities/services/auth.service'

import {
  capitalizeString,
  formatCurrency,
  groupPurchasePricing,
  packagePricing,
  formatTicketGroups,
  getPaymentOption,
  inPersonPaymentMethods,
  getAmountDue,
} from '../../../utilities/helpers'

import {
  createOrderAssignment,
  createSeasonPackageOrder,
  getCompLabels,
  getBuyerTypes,
  getPaymentIntent,
  pastPurchasers,
  customerPaymentMethods,
  getConnectionToken,
  updatePaymentIntent,
  createOrderPaymentPlan,
  changeExistingPaymentMethod,
  cancelPendingCheckoutOrder,
} from '../../../utilities/api'
import {
  isAssignmentCompLabelMethod,
  isAssignmentLabelSelectionValid,
} from '../../../utilities/buyerTypeFilters'

import Modal from 'react-bootstrap/Modal'

import {
  Confirm,
  Delete,
  Error,
  Loading,
  MarkAsComplete,
  Success,
} from '../../../components/ModalContent'
import { LoadingContainer } from '../../../components/LoadingContainer'
import ModalForm from './ModalForm'
import { constants } from '@blocktickets/shared-utils'

const { PAYMENT_TYPE, PRODUCT_TYPE, ORDER_SOURCE } = constants

export default function AssignModalForm({
  ticketType,
  event,
  eventPackage,
  selectedSeatIds,
  data,
  initialAssignTo,
  initialPaymentMethod,
  sourceOrderId,
  reloadAssignments,
  stripeAccountContext,
  onStripeAccountContextChange,
}) {
  const navigate = useNavigate()

  // Modal actions
  const { closeModal, openModal } = useModal()
  // AuthService.getOrg() parses localStorage each call and returns a new array/object,
  // which can cause effects/memos to re-run on every render. Memoize it for modal lifetime.
  const organizations = useMemo(() => AuthService.getOrg() || [], [])
  const organization = organizations?.[0]
  const terminalInfo = AuthService.getProfile()

  let stripePromise

  // Prefer deriving the connected account from the item being sold (event/package),
  // since admins can belong to multiple orgs and AuthService.getOrg()[0] may not match.
  const isProd =
    process.env.NODE_ENV === 'production' ||
    process.env.REACT_APP_STAGE === 'production'

  const paymentOrgFromSession = useMemo(() => {
    const targetOrgUUID =
      event?.organizationUUID || eventPackage?.organizationUUID
    if (!targetOrgUUID || !Array.isArray(organizations)) return undefined
    return organizations.find((org) => org?.uuid === targetOrgUUID)
  }, [event?.organizationUUID, eventPackage?.organizationUUID, organizations])

  const organizationUUIDForAssignment =
    event?.organizationUUID ||
    event?.organization?.uuid ||
    eventPackage?.organizationUUID ||
    eventPackage?.organization?.uuid

  const paymentOrg =
    event?.organization ||
    eventPackage?.organization ||
    paymentOrgFromSession ||
    organization

  // Resolve the org from AuthService (it has organization_fee_config populated)
  // since event/package org objects are not always fully populated.
  const feeConfigOrg = useMemo(() => {
    if (!paymentOrg) return organization
    const byUuid =
      paymentOrg?.uuid &&
      organizations?.find((o) => o?.uuid && o.uuid === paymentOrg.uuid)
    if (byUuid) return byUuid
    const byId =
      paymentOrg?.id &&
      organizations?.find((o) => o?.id && o.id === paymentOrg.id)
    return byId || paymentOrg || organization
  }, [paymentOrg, organization, organizations])

  const organizationFeeConfig = feeConfigOrg?.organization_fee_config

  const connectedAccountId = isProd
    ? paymentOrg?.connected_account_id
    : paymentOrg?.test_connected_account_id

  // track overlapping modals
  const [isAnotherModalOpen, setIsAnotherModalOpen] = useState(false)
  const [currentSelectedSeatIds, setCurrentSelectedSeatIds] = useState(
    selectedSeatIds || {}
  )
  const [currentData, setCurrentData] = useState(data || {})

  const [step, setStep] = useState(1)
  const [isSaving, setIsSaving] = useState(false)
  const [isValid, setIsValid] = useState(false)
  const [compLabels, setCompLabels] = useState([])
  const [selectedCompLabel, setSelectedCompLabel] = useState()
  const [buyerTypes, setBuyerTypes] = useState([])
  const [selectedBuyerType, setSelectedBuyerType] = useState()
  const [clientSecret, setClientSecret] = useState()
  const [initialClientSecret, setInitialClientSecret] = useState()
  const [card, setCard] = useState()
  const [stripe, setStripe] = useState()

  const [isLoading, setIsLoading] = useState(true)
  const [userType, setUserType] = useState('new-user')
  // one-time or payment-plan
  const [paymentSchedule, setPaymentSchedule] = useState('one-time')
  const [existingUser, setExistingUser] = useState()
  // flag for existing user has changed
  const [initialExistingUser, setInitialExistingUser] = useState()
  const [savedCards, setSavedCards] = useState()
  const [isLoadingCards, setIsLoadingCards] = useState(false)
  const [selectedCard, setSelectedCard] = useState()
  const lastPaymentIntentKeyRef = useRef(null)
  const inFlightPaymentIntentKeyRef = useRef(null)
  const pendingOrderIdRef = useRef(null)
  const assignmentCheckoutKeyRef = useRef(
    `assignment-${Date.now()}-${Math.random().toString(36).slice(2)}`
  )

  useEffect(() => {
    return () => {
      if (pendingOrderIdRef.current) {
        cancelPendingCheckoutOrder(
          pendingOrderIdRef.current,
          'abandoned'
        ).catch(() => {})
      }
    }
  }, [])

  // payment plan states
  const [installmentMonths, setInstallmentMonths] = useState()
  const [paymentPlans, setPaymentPlans] = useState({})
  const [stripeTerminal, setStripeTerminal] = useState()

  // Lifted state from Pricing
  // payment method
  // - online - invoice, pos, credit card
  // - in-person - cash, check, team-credit, e-transfer, pos
  const [paymentMethod, setPaymentMethod] = useState(initialPaymentMethod)

  const [price, setPrice] = useState('')

  // all price tiers
  const [priceTiers, setPriceTiers] = useState(
    eventPackage ? eventPackage.pricingTiers : []
  )

  // Lifted state from AssignToGroup
  const initialAssignToDefaults = {
    organizationName: initialAssignTo?.organizationName || '',
    firstName: initialAssignTo?.firstName || '',
    lastName: initialAssignTo?.lastName || '',
    email: initialAssignTo?.email || '',
    phoneNumber: initialAssignTo?.phoneNumber || null,
  }

  const [assignTo, setAssignTo] = useState(initialAssignToDefaults)
  const [isValidEmail, setIsValidEmail] = useState(true)
  const [isValidNumber, setIsValidNumber] = useState(true)
  const [isCardValid, setIsCardValid] = useState(false)
  const [reader, setReader] = useState(null)

  // due date for invoice
  const [dueDateOpt, setDueDateOpt] = useState()

  const [customDueDate, setCustomDueDate] = useState()

  // Alert state
  const [alert, setAlert] = useState({
    show: false,
    variant: 'danger',
    message: '',
  })

  useEffect(() => {
    setCurrentSelectedSeatIds(selectedSeatIds || {})
    setCurrentData(data || {})
  }, [selectedSeatIds, data])

  // Memorize total seats calculation
  const totalSeats = useMemo(() => {
    let total = 0

    if (currentSelectedSeatIds?.seats) {
      total += Object.keys(currentSelectedSeatIds.seats).length
    }

    if (currentSelectedSeatIds?.gaSeats) {
      Object.entries(currentSelectedSeatIds.gaSeats).forEach(
        ([sectionId, seatIds]) => {
          total += seatIds.length
        }
      )
    }

    return total
  }, [currentSelectedSeatIds])

  // Price breakdown
  const pricingObject = useMemo(() => {
    return event
      ? groupPurchasePricing(
          price,
          event,
          totalSeats,
          ticketType,
          paymentMethod,
          organizationFeeConfig
        )
      : packagePricing(
          eventPackage,
          totalSeats,
          paymentMethod,
          true,
          price,
          paymentOrg,
          organizationFeeConfig,
          {
            source: ORDER_SOURCE.TICKET_ASSIGNMENT,
            productType: PRODUCT_TYPE.SEASON_PACKAGES,
          }
        )
  }, [
    price,
    event,
    totalSeats,
    ticketType,
    paymentMethod,
    eventPackage,
    paymentOrg,
    organizationFeeConfig,
  ])

  const amountDue = useMemo(() => {
    const hasPaymentPlan = Object.keys(paymentPlans).length > 0
    return getAmountDue(
      pricingObject,
      paymentMethod,
      hasPaymentPlan,
      paymentPlans
    )
  }, [pricingObject, paymentMethod, paymentPlans])

  // Validation logic - validates both steps at once
  useEffect(() => {
    // get paymentOption
    const option = getPaymentOption(paymentMethod)

    // Step 1 validation: price (can be zero now to support iceDogs comps) and payment method required
    // Skip step 1 validation if complimentary or reserved (reserved behaves like comp in modal)
    const isStep1Valid =
      paymentMethod === 'COMP' || paymentMethod === 'RESERVED'
        ? true
        : (() => {
            const isPriceValid = !isNaN(Number(parseFloat(price)))

            const hasPaymentMethod =
              paymentMethod !== 'COMP' && paymentMethod !== 'RESERVED'
                ? Boolean(paymentMethod)
                : true

            const hasPaymentOption =
              paymentMethod !== 'COMP' && paymentMethod !== 'RESERVED'
                ? Boolean(option)
                : true

            const hasPaymentPlan =
              paymentSchedule === 'one-time'
                ? true
                : Object.keys(paymentPlans).length > 0

            const skipPaymentIntentCheck = Boolean(
              [
                ...inPersonPaymentMethods,
                'COMP',
                'RESERVED',
                'INVOICE',
              ].includes(paymentMethod)
            )

            const checkPaymentIntent =
              skipPaymentIntentCheck || Number(parseFloat(price)) === 0
                ? true
                : paymentMethod &&
                  ['CARD', 'POS'].includes(paymentMethod) &&
                  Boolean(clientSecret)

            const hasDueDate =
              paymentMethod === 'INVOICE'
                ? Boolean(dueDateOpt) &&
                  (dueDateOpt !== 'custom' || Boolean(customDueDate))
                : true

            console.log(
              'step 1 payment intent',
              checkPaymentIntent,
              Boolean(clientSecret),
              isPriceValid,
              price
            )

            return (
              isPriceValid &&
              hasPaymentMethod &&
              hasPaymentOption &&
              checkPaymentIntent &&
              hasPaymentPlan &&
              hasDueDate
            )
          })()

    // Step 2 validation: user information
    let isFormValid = false
    let isExistingUserType = userType === 'existing-user'
    // if user type is existing user, skip checking form
    if (isExistingUserType) {
      isFormValid = Boolean(existingUser)
    } else {
      // Step 2 validation: first name, last name, and email required
      isFormValid =
        assignTo.firstName.trim() !== '' &&
        assignTo.lastName.trim() !== '' &&
        assignTo.email.trim() !== '' &&
        isValidEmail &&
        isValidNumber
    }

    // Reserved is a hold, not a comp — do not require a hidden Comp type.
    const isCompBuyerValid = isAssignmentLabelSelectionValid({
      paymentMethod,
      compLabels,
      selectedCompLabel,
      buyerTypes,
      selectedBuyerType,
    })
    // is credit card valid
    // - credit card valid
    // - saved card selected
    const isPaymentMethodValid =
      paymentMethod === 'CARD' ? Boolean(selectedCard || isCardValid) : true

    const isStep2Valid = isFormValid && isCompBuyerValid && isPaymentMethodValid

    console.log(
      'step 2 valid',
      isFormValid,
      isCompBuyerValid,
      isPaymentMethodValid
    )

    // Both steps must be valid
    setIsValid(isStep1Valid && isStep2Valid)
    // todo add payment plan intent
  }, [
    price,
    paymentMethod,
    assignTo,
    isValidEmail,
    isValidNumber,
    selectedBuyerType,
    selectedCompLabel,
    buyerTypes,
    compLabels,
    userType,
    isCardValid,
    selectedCard,
    clientSecret,
    paymentPlans,
    paymentSchedule,
    dueDateOpt,
    customDueDate,
    existingUser,
  ])

  // reset state when user type changes
  useEffect(() => {
    const assignToDefaultObj = {
      organizationName: initialAssignToDefaults.organizationName,
      firstName: initialAssignToDefaults.firstName,
      lastName: initialAssignToDefaults.lastName,
      email: initialAssignToDefaults.email,
      phoneNumber: initialAssignToDefaults.phoneNumber,
    }
    if (userType === 'existing-user') {
      setAssignTo(assignToDefaultObj)
    } else {
      if (existingUser) {
        setExistingUser()
        setAssignTo(assignToDefaultObj)
        setSavedCards()
      }
    }
  }, [
    userType,
    existingUser,
    initialAssignToDefaults.organizationName,
    initialAssignToDefaults.firstName,
    initialAssignToDefaults.lastName,
    initialAssignToDefaults.email,
    initialAssignToDefaults.phoneNumber,
  ])

  // loads only on initial render or when price changes
  useEffect(() => {
    // load payment intent if assignment is not complimentary or reserved
    if (paymentMethod !== 'COMP' && paymentMethod !== 'RESERVED') {
      // loadTicketAssignmentPaymentIntent();
      // loadPaymentIntent();
    }

    // reset when payment method changes
    setClientSecret()
    setIsCardValid(false)
    lastPaymentIntentKeyRef.current = null
    inFlightPaymentIntentKeyRef.current = null
  }, [paymentMethod])

  useEffect(() => {
    // Reset valid email error state when email changes
    setIsValidEmail(true)
  }, [assignTo.email])

  useEffect(() => {
    // Reset valid phone number error state when phone number changes
    setIsValidNumber(true)
  }, [assignTo.phoneNumber])

  // reset payment plans when payment schedule (one time/payment plans) changes
  useEffect(() => {
    setPaymentPlans({})
    setInstallmentMonths()
  }, [paymentSchedule])

  // reset payment schedule (one-time/payment plans) when payment method (card, check etc.) changes
  useEffect(() => {
    setPaymentSchedule('one-time')
    if (isAssignmentCompLabelMethod(paymentMethod)) {
      loadCompLabels()
    } else {
      loadBuyerTypes()
    }
  }, [paymentMethod])

  // new existing user
  useEffect(() => {
    setIsCardValid(false)
    setSelectedCard()
    // get saved cards
    if (existingUser) {
      // if payment method is credit card
      if (paymentMethod === 'CARD') {
        // if different user
        // or haven't gotten saved cards yet (first time)
        if (!isEqual(existingUser, initialExistingUser) || !savedCards) {
          loadSavedCards(existingUser?.email)
        }
      }
    }
  }, [existingUser, initialExistingUser, paymentMethod])

  useEffect(() => {
    if (existingUser) {
      const obj = {
        organizationName: '',
        firstName: existingUser?.firstName,
        lastName: existingUser?.lastName,
        email: existingUser?.email,
        phoneNumber: existingUser?.phoneNumber,
      }
      setAssignTo(obj)
    }
  }, [existingUser])

  useEffect(() => {
    if (!installmentMonths) return

    const paymentCalculations = calculations.splitAmount(
      pricingObject?.purchasePriceWithTaxAndFees,
      installmentMonths
    )

    const plans = [...Array(installmentMonths)].reduce((acc, _, index) => {
      const id = index + 1

      acc[id] = {
        id,
        amount: paymentCalculations[index].toFixed(2),
        dueDate: moment()
          .startOf('day')
          .add(id === 1 ? index : id - 1, 'months')
          .toDate(),
      }

      return acc
    }, {})

    setPaymentPlans(plans)
  }, [installmentMonths, pricingObject?.purchasePriceWithTaxAndFees])

  const setPaymentDate = (date, id) => {
    let paymentPlansCopy = paymentPlans
    let keys = Object.keys(paymentPlans)

    // add 1 to month to payment dates
    for (let key of keys) {
      paymentPlansCopy[key].dueDate = new Date(
        moment(date)
          .startOf('day')
          .add(paymentPlansCopy[key].id - 1, 'months')
          .format()
      )
    }

    setPaymentPlans({ ...paymentPlansCopy })
  }

  const loadSavedCards = (userEmail) => {
    setIsLoadingCards(true)
    customerPaymentMethods(connectedAccountId, userEmail)
      .then((res) => {
        const methods = res?.data?.paymentMethods || res?.data?.data || []
        setSavedCards(Array.isArray(methods) ? methods : [])
      })
      .catch((err) => {
        console.error(err)
        setSavedCards([])
      })
      .finally(() => setIsLoadingCards(false))
  }

  const loadPaymentIntent = () => {
    if (!price) return
    const plansKey = Object.values(paymentPlans || {})
      .map((plan) => `${plan?.id}:${plan?.amount}`)
      .join('|')
    const requestKey = [
      paymentMethod,
      paymentSchedule,
      price,
      amountDue,
      totalSeats,
      plansKey,
      userType,
    ].join(':')
    if (
      inFlightPaymentIntentKeyRef.current === requestKey ||
      lastPaymentIntentKeyRef.current === requestKey
    ) {
      return
    }
    inFlightPaymentIntentKeyRef.current = requestKey
    // Reset clientSecret when paymentMethod changes
    setClientSecret('')
    // Checks if payment plans are set
    const hasPaymentPlan = Object.keys(paymentPlans).length > 0
    const makePurchaseDetailsForDraft = (selectedSeatIdsDraft, packageUUID) => {
      if (!packageUUID) return {}
      if (selectedSeatIdsDraft?.gaSeats) {
        const targetSectionId = Object.keys(selectedSeatIdsDraft.gaSeats)[0]
        const targetSeatIds = (
          selectedSeatIdsDraft.gaSeats[targetSectionId] || []
        ).filter(Boolean)
        const desiredTicketCount = targetSeatIds.length
        return {
          GA: true,
          sectionId: targetSectionId,
          desiredTicketCount,
          ...(targetSeatIds.length ? { targetSeatIds } : {}),
        }
      }
      if (selectedSeatIdsDraft?.seats) {
        return {
          GA: false,
          targetSeatIds: Object.keys(selectedSeatIdsDraft.seats),
        }
      }
      return {}
    }
    const safeNum = (value) => {
      const n = Number(value)
      return Number.isFinite(n) ? n : 0
    }

    const assignment = {}
    assignment['assignTo'] = assignTo

    delete event?.page_views
    delete event?.tickets
    // Backend needs organization.uuid and/or connected_account_id to resolve destinationAccount; merge paymentOrg so those are always present.
    assignment['event'] = event
      ? {
          ...event,
          organization: paymentOrg
            ? { ...paymentOrg, ...event?.organization }
            : event?.organization,
        }
      : event
    assignment['eventPackage'] = eventPackage
    assignment['totalSeats'] = totalSeats
    assignment['unitTicketPrice'] =
      paymentMethod === 'COMP' || paymentMethod === 'RESERVED'
        ? 0
        : Number.parseFloat(price) || 0
    assignment['price'] = getAmountDue(
      pricingObject,
      paymentMethod,
      hasPaymentPlan,
      paymentPlans
    )
    // in-person/online
    assignment['paymentOption'] = getPaymentOption(paymentMethod)
    assignment['paymentMethod'] = PAYMENT_TYPE[paymentMethod]
    assignment['paymentPlans'] = paymentPlans
    assignment['selectedSeats'] = currentSelectedSeatIds
    assignment['checkoutKey'] = assignmentCheckoutKeyRef.current

    const cardLikePaymentMethods = new Set(['CARD', 'INVOICE', 'POS'])
    if (cardLikePaymentMethods.has(String(paymentMethod || '').toUpperCase())) {
      const pf = Number(pricingObject?.paymentProcessingFee)
      if (Number.isFinite(pf) && pf > 0) {
        assignment.processingFee = pf
      }
    }

    const assignTicketsType =
      paymentMethod === 'COMP'
        ? 'comp'
        : paymentMethod === 'RESERVED'
          ? 'reserved'
          : ticketType
    const selectedBuyerOrComp = selectedBuyerType ?? selectedCompLabel
    const commonDraftData = {
      canReleaseTickets: true,
      organizationUUID: event
        ? event?.organizationUUID
        : eventPackage?.organizationUUID,
      paymentMethod: PAYMENT_TYPE[paymentMethod],
      paymentType: paymentSchedule,
      paymentOption: getPaymentOption(paymentMethod),
      assignTicketsType,
      selectedSeats: currentSelectedSeatIds,
      purchaseDetails: makePurchaseDetailsForDraft(
        currentSelectedSeatIds,
        eventPackage?.uuid
      ),
      assignTo,
      selectedBuyerType: selectedBuyerOrComp,
      fromOrderId: sourceOrderId,
      connectedAccountId,
      paymentIntentId: '',
      checkoutKey: assignmentCheckoutKeyRef.current,
      ...(pendingOrderIdRef.current
        ? { pendingOrderId: pendingOrderIdRef.current }
        : {}),
    }
    if (hasPaymentPlan) {
      const fullTotal = safeNum(pricingObject?.purchasePriceWithTaxAndFees)
      assignment.orderDraft = {
        endpoint: 'create-payment-plan-assignment',
        payload: {
          eventUUID: event?.uuid,
          packageUUID: eventPackage?.uuid,
          details: {
            ...commonDraftData,
            groupTicketsPrice: fullTotal,
            installments: Object.values(paymentPlans),
            hasPaymentPlan: true,
            currency: paymentOrg?.currency ?? organization?.currency ?? 'usd',
            pricing: {
              total: fullTotal,
              salesTax: safeNum(pricingObject?.salesTax),
              serviceFee: safeNum(pricingObject?.serviceFee),
              processingFee: safeNum(pricingObject?.paymentProcessingFee),
              facilityFee: safeNum(pricingObject?.ticketFacilityFee),
              chargeBackProtection: safeNum(
                pricingObject?.chargeBackProtection
              ),
              net: safeNum(pricingObject?.yourPayout),
              ticketCount: safeNum(totalSeats),
              amountDueNow: safeNum(assignment.price),
            },
          },
        },
      }
    } else {
      assignment.orderDraft = {
        endpoint:
          eventPackage && !sourceOrderId
            ? 'create-season-package-order'
            : 'create-order-assignment',
        payload: {
          eventUUID: event?.uuid,
          packageUUID: eventPackage?.uuid,
          invoice: {
            ...commonDraftData,
            groupTicketsPrice: safeNum(assignment.price),
            dueDate: undefined,
            pricing: {
              total: safeNum(pricingObject?.purchasePriceWithTaxAndFees),
              salesTax: safeNum(pricingObject?.salesTax),
              serviceFee: safeNum(pricingObject?.serviceFee),
              processingFee: safeNum(pricingObject?.paymentProcessingFee),
              facilityFee: safeNum(pricingObject?.ticketFacilityFee),
              chargeBackProtection: safeNum(
                pricingObject?.chargeBackProtection
              ),
              net: safeNum(pricingObject?.yourPayout),
              ticketCount: safeNum(totalSeats),
              amountDueNow: safeNum(assignment.price),
            },
          },
        },
      }
    }

    getPaymentIntent({ assignment })
      .then((res) => {
        const pendingOrderId = res?.data?.metadata?.order_id
        if (pendingOrderId) pendingOrderIdRef.current = pendingOrderId
        const isDestinationCharge = Boolean(
          res?.data?.transfer_data?.destination
        )
        const desiredStripeAccountContext = isDestinationCharge
          ? null
          : connectedAccountId
        const didContextChange =
          desiredStripeAccountContext !== stripeAccountContext

        if (typeof onStripeAccountContextChange === 'function') {
          onStripeAccountContextChange(desiredStripeAccountContext)
        }

        if (didContextChange) {
          // Ensure Elements reinitializes with the new account context
          // before using this client secret for confirmCardPayment.
          setClientSecret('')
          setTimeout(() => {
            setClientSecret(res?.data?.client_secret)
          }, 50)
        } else {
          setClientSecret(res?.data?.client_secret)
        }
        lastPaymentIntentKeyRef.current = requestKey
      })
      .catch((err) => console.error(err))
      .finally(() => {
        if (inFlightPaymentIntentKeyRef.current === requestKey) {
          inFlightPaymentIntentKeyRef.current = null
        }
      })
  }

  useEffect(() => {
    // Quick exit conditions - no intent needed [invoice, cash, check, etc]
    // if price is 0
    if (shouldSkipPaymentIntent()) {
      console.log('No payment intent needed')
      lastPaymentIntentKeyRef.current = null
      inFlightPaymentIntentKeyRef.current = null
      return
    }

    // Main cases - payment intent / terminal needed
    if (isCreditCardOnlineOneTime()) {
      //   if (!eventPackage) loadPaymentIntent();
      if (price) loadPaymentIntent()
      console.log('load payment card intent')
      return
    }
    console.log(
      'isCreditCardOnlinePaymentPlan() ',
      isCreditCardOnlinePaymentPlan()
    )
    if (isCreditCardOnlinePaymentPlan()) {
      //   if (!eventPackage) loadPaymentIntent();
      if (price) loadPaymentIntent()
      console.log('load payment plan intent')
      return
    }

    if (isPosTerminalCase()) {
      //   if (!eventPackage) loadPaymentIntent();
      if (price) loadPaymentIntent()
      console.log('init terminal intent')
      return
    }
  }, [paymentMethod, paymentPlans, price])

  /* ────────────────────────────────────────────── */
  /*                Helper functions                */
  /* ────────────────────────────────────────────── */

  function shouldSkipPaymentIntent() {
    const hasPaymentPlan = Object.keys(paymentPlans).length > 0
    // Any in-person method
    // if price is 0
    if (
      (getPaymentOption(paymentMethod) === 'in-person' && !hasPaymentPlan) ||
      Number(parseFloat(price)) === 0
    ) {
      return true
    }

    return false
  }

  function isCreditCardOnlineOneTime() {
    const hasPaymentPlan = Object.keys(paymentPlans).length > 0
    return (
      paymentSchedule === 'one-time' &&
      getPaymentOption(paymentMethod) === 'online' &&
      paymentMethod === 'CARD' &&
      userType === 'new-user' &&
      !hasPaymentPlan
    )
  }

  function isCreditCardOnlinePaymentPlan() {
    const hasPaymentPlan = Object.keys(paymentPlans).length > 0
    return (
      paymentMethod === 'CARD' &&
      getPaymentOption(paymentMethod) === 'online' &&
      paymentSchedule === 'payment-plan' &&
      userType === 'new-user' &&
      hasPaymentPlan
    )
  }

  function isPosTerminalCase() {
    const hasPaymentPlan = Object.keys(paymentPlans).length > 0
    return (
      paymentMethod === 'POS' &&
      (paymentSchedule === 'one-time' ||
        (paymentSchedule === 'payment-plan' && hasPaymentPlan))
    )
  }

  const loadCompLabels = () => {
    const organizationUUID = event
      ? event?.organizationUUID
      : eventPackage?.organizationUUID

    getCompLabels(organizationUUID)
      .then((res) => {
        setCompLabels(res?.data?.data)
        console.log('compLabels ', res?.data?.data)
      })
      .catch((err) => console.error(err))
      .finally(() => setIsLoading(false))
  }

  const loadBuyerTypes = () => {
    const organizationUUID = event
      ? event?.organizationUUID
      : eventPackage?.organizationUUID

    getBuyerTypes(organizationUUID, ticketType)
      .then((res) => setBuyerTypes(res?.data?.data))
      .catch((err) => console.error(err))
      .finally(() => setIsLoading(false))
  }

  const loadExistingUsers = (query) => {
    const organizationUUID = event
      ? event?.organizationUUID
      : eventPackage?.organizationUUID

    return pastPurchasers(organizationUUID, query)
      .then((res) => {
        return res.data?.map((user) => ({
          label: capitalizeString(`${user.firstName} ${user.lastName}`),
          email: user.email,
          firstName: user?.firstName,
          lastName: user?.lastName,
          phoneNumber: user?.phoneNumber,
          custom: user?.email,
        }))
      })
      .catch((err) => {
        console.error(err)
        throw err
      })
  }

  // get selected tickets
  const idsToTicketLike = (seatIdsMap, seatData) => {
    const tickets = []

    // Handle GA seats
    if (seatIdsMap.gaSeats) {
      Object.entries(seatIdsMap.gaSeats).forEach(([sectionId, seatIds]) => {
        const section = seatData.sections[sectionId]
        if (section) {
          // Create a ticket for each GA seat in this section
          seatIds.forEach((seatId) => {
            tickets.push({
              generalAdmission: true,
              sectionNumber: section.sectionNumber,
              seatId: seatId,
            })
          })
        }
      })
    }

    // Handle assigned seats
    if (seatIdsMap.seats) {
      Object.keys(seatIdsMap.seats).forEach((seatId) => {
        const seat = seatData.seats[seatId]
        if (seat) {
          tickets.push({
            generalAdmission: false,
            sectionNumber: seat.sectionNumber,
            rowNumber: seat.rowNumber,
            seatNumber: seat.seatNumber,
            seatId: seatId,
            packageUUID: seat?.package_uuid || eventPackage?.uuid,
          })
        }
      })
    }

    return tickets
  }

  const ticketLikeArr = idsToTicketLike(
    currentSelectedSeatIds || {},
    currentData || { seats: {}, sections: {} }
  )
  const formattedGroups = formatTicketGroups(ticketLikeArr)

  const toSeatMatchKey = (
    sectionNumber,
    rowNumber,
    seatNumber,
    packageUUID
  ) => {
    const norm = (v) =>
      String(v ?? '')
        .trim()
        .toLowerCase()
    return `${norm(sectionNumber)}|${norm(rowNumber)}|${norm(seatNumber)}|${norm(packageUUID)}`
  }

  const buildSelectedPackageTicketMatchers = (
    selectedSeatIdsMap,
    seatData,
    packageUUID
  ) => {
    const seatMap = selectedSeatIdsMap?.seats || {}
    const seen = new Set()
    return Object.keys(seatMap).reduce((acc, seatId) => {
      const seat = seatData?.seats?.[seatId]
      if (!seat) return acc
      const matcher = {
        seatId: seatId,
        sectionNumber: seat?.sectionNumber,
        rowNumber: seat?.rowNumber,
        seatNumber: seat?.seatNumber,
        packageUUID: seat?.package_uuid || packageUUID,
      }
      const key = toSeatMatchKey(
        matcher.sectionNumber,
        matcher.rowNumber,
        matcher.seatNumber,
        matcher.packageUUID
      )
      if (seen.has(key)) return acc
      seen.add(key)
      acc.push(matcher)
      return acc
    }, [])
  }

  const handleAssignToChange = (field, value) => {
    setAssignTo((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const openLoading = (title, icon, btnText) =>
    openModal(
      Loading,
      {
        title,
        icon,
        btnText,
        onClose: closeModal,
      },
      { className: 'modal-md', contentClassName: '' }
    )

  const createAssignment = async (paymentIntentId, dueDate) => {
    const makePurchaseDetails = (selectedSeatIds, packageUUID) => {
      let purchaseDetails = {}

      // Early return for eventTickets
      if (!packageUUID) return purchaseDetails

      // Since for packages we just allow basically one section at a time
      if (selectedSeatIds.gaSeats) {
        const targetSectionId = Object.keys(selectedSeatIds.gaSeats)[0]
        const targetSeatIds = (
          selectedSeatIds.gaSeats[targetSectionId] || []
        ).filter(Boolean)
        const desiredTicketCount = targetSeatIds.length
        purchaseDetails = {
          GA: true,
          sectionId: targetSectionId,
          desiredTicketCount,
          ...(targetSeatIds.length ? { targetSeatIds } : {}),
        }
      } else if (selectedSeatIds.seats) {
        purchaseDetails = {
          GA: false,
          targetSeatIds: Object.keys(selectedSeatIds.seats),
        }
      }

      return purchaseDetails
    }

    if (existingUser) assignTo['existingUser'] = existingUser.email

    const hasPaymentPlan = Object.keys(paymentPlans).length > 0

    const safeNum = (v) => {
      const n = Number(v)
      return Number.isFinite(n) ? n : 0
    }

    // Snapshot pricing so backend can persist exactly what the UI shows.
    // This is important for ticket_assignment flows where we want totals to always match.
    const pricingSnapshot = {
      total: safeNum(pricingObject?.purchasePriceWithTaxAndFees),
      salesTax: safeNum(pricingObject?.salesTax),
      serviceFee: safeNum(pricingObject?.serviceFee),
      processingFee: safeNum(pricingObject?.paymentProcessingFee),
      facilityFee: safeNum(pricingObject?.ticketFacilityFee),
      chargeBackProtection: safeNum(pricingObject?.chargeBackProtection),
      // Net is what the organizer receives (excludes buyer-facing fees)
      net: safeNum(pricingObject?.yourPayout),
      ticketCount: safeNum(totalSeats),
      amountDueNow: safeNum(amountDue),
    }

    // Construct the order data
    const selectedPackageTicketMatchers = buildSelectedPackageTicketMatchers(
      currentSelectedSeatIds,
      currentData,
      eventPackage?.uuid
    )

    const data = {
      eventUUID: event?.uuid,
      packageUUID: eventPackage?.uuid,
      invoice: {
        canReleaseTickets: true, // ??
        organizationUUID: event
          ? event?.organizationUUID
          : eventPackage?.organizationUUID,
        // Payment details
        paymentMethod: PAYMENT_TYPE[paymentMethod],
        paymentType: paymentSchedule,
        // in-person/online
        paymentOption: getPaymentOption(paymentMethod),
        assignTicketsType:
          paymentMethod === 'COMP'
            ? 'comp'
            : paymentMethod === 'RESERVED'
              ? 'reserved'
              : ticketType,
        groupTicketsPrice: getAmountDue(
          pricingObject,
          paymentMethod,
          hasPaymentPlan,
          paymentPlans
        ),
        // What to who
        selectedSeats: currentSelectedSeatIds,
        selectedPackageTicketMatchers,
        purchaseDetails: makePurchaseDetails(
          currentSelectedSeatIds,
          eventPackage?.uuid
        ),
        assignTo,
        selectedBuyerType: selectedBuyerType,
        selectedCompLabel: selectedCompLabel,
        paymentIntentId: paymentIntentId,
        pendingOrderId: pendingOrderIdRef.current,
        fromOrderId: sourceOrderId,
        connectedAccountId: connectedAccountId,
        dueDate,
        pricing: pricingSnapshot,
      },
    }

    // Reserved assignment completion flows (fromOrderId present) must use the
    // assignment endpoint so deselected package tickets are removed correctly.
    const request =
      eventPackage && !sourceOrderId
        ? createSeasonPackageOrder(data)
        : createOrderAssignment(data)

    return await request
  }

  const createOrder = (paymentIntentId = '', callback, dueDate = undefined) => {
    console.log('create assignment')
    createAssignment(paymentIntentId, dueDate)
      .then((res) => {
        console.log('create assignment then', res)
        setIsSaving(false)
        if (callback) callback()
      })
      .catch((err) => {
        console.log('errro', err)
        setIsSaving(false)
        if (!isSaving)
          openError(
            'Something went wrong!',
            'Could not assign tickets to user.'
          )
      })
  }

  const createPaymentPlanAssignment = async (paymentIntentId) => {
    const updatedIntent = await updatePaymentIntent({
      assignTo,
      paymentIntentId,
      connectedAccountId,
    })

    if (updatedIntent?.status !== 200) {
      console.log('Payment did not fully succeed:', updatedIntent.status)
      return
    }

    const makePurchaseDetails = (selectedSeatIds, packageUUID) => {
      let purchaseDetails = {}

      // Early return for eventTickets
      if (!packageUUID) return purchaseDetails

      // Since for packages we just allow basically one section at a time
      if (selectedSeatIds.gaSeats) {
        const targetSectionId = Object.keys(selectedSeatIds.gaSeats)[0]
        const targetSeatIds = (
          selectedSeatIds.gaSeats[targetSectionId] || []
        ).filter(Boolean)
        const desiredTicketCount = targetSeatIds.length
        purchaseDetails = {
          GA: true,
          sectionId: targetSectionId,
          desiredTicketCount,
          ...(targetSeatIds.length ? { targetSeatIds } : {}),
        }
      } else if (selectedSeatIds.seats) {
        purchaseDetails = {
          GA: false,
          targetSeatIds: Object.keys(selectedSeatIds.seats),
        }
      }

      return purchaseDetails
    }

    if (existingUser) assignTo['existingUser'] = existingUser.email

    const safeNum = (v) => {
      const n = Number(v)
      return Number.isFinite(n) ? n : 0
    }

    const fullTotal = safeNum(pricingObject?.purchasePriceWithTaxAndFees)
    const pricingSnapshot = {
      total: fullTotal,
      salesTax: safeNum(pricingObject?.salesTax),
      serviceFee: safeNum(pricingObject?.serviceFee),
      processingFee: safeNum(pricingObject?.paymentProcessingFee),
      facilityFee: safeNum(pricingObject?.ticketFacilityFee),
      chargeBackProtection: safeNum(pricingObject?.chargeBackProtection),
      net: safeNum(pricingObject?.yourPayout),
      ticketCount: safeNum(totalSeats),
      // First installment / due-now amount (what the PaymentIntent was created for)
      amountDueNow: safeNum(amountDue),
    }

    // Construct the order data
    const selectedPackageTicketMatchers = buildSelectedPackageTicketMatchers(
      currentSelectedSeatIds,
      currentData,
      eventPackage?.uuid
    )

    const data = {
      eventUUID: event?.uuid,
      packageUUID: eventPackage?.uuid,
      details: {
        canReleaseTickets: true, // ??
        organizationUUID: event
          ? event?.organizationUUID
          : eventPackage?.organizationUUID,
        // Payment details
        paymentMethod: PAYMENT_TYPE[paymentMethod],
        paymentType: paymentSchedule,
        // in-person/online
        paymentOption: getPaymentOption(paymentMethod),
        assignTicketsType:
          paymentMethod === 'COMP'
            ? 'comp'
            : paymentMethod === 'RESERVED'
              ? 'reserved'
              : ticketType,
        // For payment plans, store the FULL order total on the backend,
        // but keep the "due now" amount in `pricing.amountDueNow`.
        groupTicketsPrice: fullTotal,
        // What to who
        selectedSeats: currentSelectedSeatIds,
        selectedPackageTicketMatchers,
        purchaseDetails: makePurchaseDetails(
          currentSelectedSeatIds,
          eventPackage?.uuid
        ),
        assignTo,
        selectedBuyerType: selectedBuyerType,
        selectedCompLabel: selectedCompLabel,
        paymentIntentId: paymentIntentId,
        pendingOrderId: pendingOrderIdRef.current,
        fromOrderId: sourceOrderId,
        installments: Object.values(paymentPlans),
        hasPaymentPlan: true,
        connectedAccountId,
        currency: paymentOrg?.currency ?? organization?.currency ?? 'usd',
        pricing: pricingSnapshot,
      },
    }

    const request = createOrderPaymentPlan(data)

    return await request
  }

  const createPaymentPlanOrder = (paymentIntentId = '', callback) => {
    createPaymentPlanAssignment(paymentIntentId)
      .then((res) => {
        console.log('create payment plan assignment then', res)
        setIsSaving(false)
        if (callback) callback()
      })
      .catch((err) => {
        console.log('errro', err)
        setIsSaving(false)
        if (!isSaving)
          openError(
            'Something went wrong!',
            'Could not assign tickets to user.'
          )
      })
  }

  const handleCardPayment = async () => {
    // process card payments
    if (getPaymentOption(paymentMethod) !== 'online') {
      // dont use if not card payment
      console.warn('Non card payment selected')
      return
    }

    setIsSaving(true)

    const email = assignTo.email

    const { error, paymentIntent } = await stripe.confirmCardPayment(
      clientSecret,
      {
        payment_method: {
          card,
          billing_details: { email },
        },
      }
    )

    if (error) {
      console.error(error.message, ' Payment failed.')
      setIsSaving(false)
    } else if (paymentIntent && paymentIntent.status === 'succeeded') {
      // Create Order Here
      createOrder(paymentIntent.id)
    }
  }

  const showPaymentSuccess = () => {
    showSuccess(
      'Payment received successfully!',
      'Amount paid',
      getAmountDue(
        pricingObject,
        paymentMethod,
        Object.keys(paymentPlans).length > 0,
        paymentPlans
      )
    )
  }

  const showSuccess = (title, description, amount) => {
    openSuccess(
      title,
      description,
      amount ? `${formatCurrency(amount)}` : undefined,
      event?.uuid,
      eventPackage?.uuid
    )
  }

  const showAssignmentSuccess = () => {
    showSuccess('Ticket assigned successfully', null, null)
  }

  const onSuccessClose = (redirectURL) => {
    closeModal()
    navigate(redirectURL)
    if (typeof reloadAssignments === 'function') {
      reloadAssignments(0)
    }
  }

  const openSuccess = (
    title,
    description,
    highlightText,
    eventUUID,
    packageUUID
  ) => {
    // const description = `A copy of the invoice and receipt has been sent to ${email}`;
    const eventRedirectURL = `/event/${eventUUID}/assign-tickets`
    const packageRedirectURL = `/package/${packageUUID}/assign-packages`
    const redirectURL = eventUUID ? eventRedirectURL : packageRedirectURL
    openModal(
      Success,
      {
        title,
        description,
        highlightText,
        onClose: () => onSuccessClose(redirectURL),
      },
      { className: 'modal-md', contentClassName: '' },
      { replace: true }
    )
  }

  const openError = (title, description) => {
    openModal(
      Error,
      {
        title,
        description,
        onClose: closeModal,
      },
      { className: 'modal-md', contentClassName: '' },
      { replace: true }
    )
  }

  const openMarkAsComplete = (paymentType) => {
    openModal(
      MarkAsComplete,
      {
        paymentType,
        // Must match the exact amount we send to backend for non-card methods
        // (e.g. TEAM_CREDIT should not include Stripe processing fees).
        amountToCollect: amountDue,
        createOrder,
        showAssignmentSuccess,
        onClose: () => closeModal(),
      },
      { className: 'modal-md', contentClassName: '' },
      { replace: true }
    )
  }

  const onDeleteClose = () => {
    setIsAnotherModalOpen(false)
    closeModal()
  }

  const openDelete = (id) => {
    setIsAnotherModalOpen(true)
    openModal(
      Delete,
      {
        entity: 'saved card',
        text: 'This card will no longer be saved for future purchases.',
        id,
        handleDelete: handleDeleteCard,
        onClose: () => onDeleteClose(),
      },
      { className: 'modal--lg', contentClassName: '' },
      { replace: false }
    )
  }

  const handleDeleteCard = (id) => {
    return new Promise((resolve, reject) => {
      setSavedCards(savedCards.filter((card) => card.id != id))
      resolve()
    })
  }

  const onRemoveSeat = (section, row, seat, ticketInfo = {}) => {
    setIsAnotherModalOpen(true)
    openModal(
      Confirm,
      {
        title: 'Remove seat?',
        description: `Are you sure you want to remove Sec ${section}, Row ${row}, Seat ${seat} from this order?`,
        onConfirm: () => {
          const explicitSeatId =
            ticketInfo?.seatId || ticketInfo?.seat_id || ticketInfo?.id
          const ticketPackageUUID =
            ticketInfo?.packageUUID ||
            ticketInfo?.package_uuid ||
            eventPackage?.uuid
          const sectionNorm = String(section).trim()
          const rowNorm = String(row).trim()
          const seatNorm = String(seat).trim()
          const seatEntries = Object.entries(currentData?.seats || {})
          const seatIdToRemove =
            explicitSeatId ||
            seatEntries.find(([, seatInfo]) => {
              const packageMatch =
                !ticketPackageUUID ||
                String(seatInfo?.package_uuid || eventPackage?.uuid).trim() ===
                  String(ticketPackageUUID).trim()
              return (
                packageMatch &&
                String(seatInfo?.sectionNumber).trim() === sectionNorm &&
                String(seatInfo?.rowNumber).trim() === rowNorm &&
                String(seatInfo?.seatNumber).trim() === seatNorm
              )
            })?.[0]

          if (seatIdToRemove) {
            setCurrentSelectedSeatIds((prev) => {
              const prevSeats = { ...(prev?.seats || {}) }
              delete prevSeats[seatIdToRemove]
              return { ...prev, seats: prevSeats }
            })

            setCurrentData((prev) => {
              const nextSeats = { ...(prev?.seats || {}) }
              delete nextSeats[seatIdToRemove]
              return { ...prev, seats: nextSeats }
            })
          }

          setIsAnotherModalOpen(false)
          closeModal()
        },
        onClose: () => {
          setIsAnotherModalOpen(false)
          closeModal()
        },
      },
      { className: 'modal-md', contentClassName: '' },
      { replace: false }
    )
  }

  // button text
  const getText = () => {
    switch (paymentMethod) {
      case 'invoice':
        return 'Send invoice'
      case 'pos-terminal':
        return 'Continue to POS Terminal'
      case 'credit-card':
        return 'Authorize payment'
      default:
        return 'Assign tickets'
    }
  }

  //   const initTerminal = async () => {
  //     // Create PaymentIntent on backend and get client secret
  //     await loadPaymentIntent();
  //     // 1) Load the SDK (this is NOT the terminal instance)
  //     const StripeTerminal = await loadStripeTerminal();

  //     if (!StripeTerminal) {
  //       throw new Error("Stripe Terminal JS SDK failed to load.");
  //     }
  //     // 2) Create the terminal instance (THIS has discoverReaders)
  //     const terminal = StripeTerminal.create({
  //       onFetchConnectionToken: async () => {
  //         const response = await getConnectionToken();
  //         const data = response.data;
  //         return data.secret; // must be a string
  //       },
  //       onUnexpectedReaderDisconnect: () => {
  //         console.warn("Reader disconnected unexpectedly");
  //       },
  //     });

  //     if (!terminal) {
  //       console.error("Failed to load Stripe Terminal");
  //       return;
  //     }
  //     setStripeTerminal(terminal);
  //     // Discover readers
  //     const discoverResult = await terminal.discoverReaders({
  //       simulated: process.env.REACT_APP_STAGE !== "production", // set true for testing without hardware
  //       location: terminalInfo.location, // optional
  //     });

  //     if (discoverResult.error) {
  //       console.error(discoverResult.error);
  //       return;
  //     }

  //     // Connect to the first reader (or let user choose)
  //     const reader = discoverResult.discoveredReaders[0];
  //     const connectResult = await terminal.connectReader(reader);

  //     if (connectResult.error) {
  //       console.error(connectResult.error);
  //     } else {
  //       setReader(connectResult.reader);
  //     }
  //   };

  const processTerminalPayment = async () => {
    // 1. Collect card
    const collectResult =
      await stripeTerminal.collectPaymentMethod(clientSecret)

    if (collectResult.error) {
      console.error(collectResult.error)
      throw collectResult.error
    }
    // 2. Process payment
    const processResult = await stripeTerminal.processPayment(
      collectResult.paymentIntent
    )

    if (processResult.error) {
      console.error(processResult.error)
      throw collectResult.error
    }

    return processResult
  }

  const chargePaymentMethod = async () => {
    const data = {}
    data['assignTo'] = assignTo
    data['amount'] = pricingObject?.purchasePriceWithTaxAndFees
    data['connectedAccountId'] = connectedAccountId
    data['paymentMethodId'] = selectedCard
    data['currency'] = organization.currency ?? 'usd'
    data['serviceFee'] = Number(pricingObject?.serviceFee) || 0
    const processingFee = Number(pricingObject?.paymentProcessingFee)
    if (Number.isFinite(processingFee) && processingFee > 0) {
      data['processingFee'] = processingFee
    }
    const savedCardDraft = {
      canReleaseTickets: true,
      organizationUUID: event
        ? event?.organizationUUID
        : eventPackage?.organizationUUID,
      paymentMethod: PAYMENT_TYPE[paymentMethod],
      paymentType: paymentSchedule,
      paymentOption: getPaymentOption(paymentMethod),
      assignTicketsType: ticketType,
      selectedSeats: currentSelectedSeatIds,
      assignTo,
      selectedBuyerType: selectedBuyerType ?? selectedCompLabel,
      fromOrderId: sourceOrderId,
      connectedAccountId,
      groupTicketsPrice: data.amount,
      checkoutKey: assignmentCheckoutKeyRef.current,
      ...(pendingOrderIdRef.current
        ? { pendingOrderId: pendingOrderIdRef.current }
        : {}),
    }
    data['assignment'] = {
      assignTo,
      event,
      eventPackage,
      checkoutKey: assignmentCheckoutKeyRef.current,
      selectedSeats: currentSelectedSeatIds,
      totalSeats,
      price: data.amount,
      paymentMethod: 'card',
      paymentOption: 'online',
      orderDraft: {
        endpoint:
          eventPackage && !sourceOrderId
            ? 'create-season-package-order'
            : 'create-order-assignment',
        payload: {
          eventUUID: event?.uuid,
          packageUUID: eventPackage?.uuid,
          invoice: savedCardDraft,
        },
      },
    }

    try {
      const res = await changeExistingPaymentMethod(data)
      const pendingOrderId = res?.data?.metadata?.order_id
      if (pendingOrderId) pendingOrderIdRef.current = pendingOrderId
      return res
    } catch (err) {
      console.error('chargePaymentMethod: Error ', err)
    }
  }

  // Creating a new order
  return (
    <Modal.Body
      className={`d-flex-column ${isAnotherModalOpen ? 'modal-opac' : ''}`}
    >
      {isLoading ? (
        <LoadingContainer />
      ) : (
        <ModalForm
          selectedTickets={formattedGroups}
          isPackage={Boolean(eventPackage)}
          ticketType={ticketType}
          price={price}
          setPrice={setPrice}
          priceTiers={priceTiers}
          paymentMethod={paymentMethod}
          setPaymentMethod={setPaymentMethod}
          paymentSchedule={paymentSchedule}
          setPaymentSchedule={setPaymentSchedule}
          pricingObject={pricingObject}
          isComplimentary={
            paymentMethod === 'COMP' || paymentMethod === 'RESERVED'
          }
          assignTo={assignTo}
          onAssignToChange={handleAssignToChange}
          isValidEmail={isValidEmail}
          setIsValidEmail={setIsValidEmail}
          isValidNumber={isValidNumber}
          setIsValidNumber={setIsValidNumber}
          buyerTypes={buyerTypes}
          selectedBuyerType={selectedBuyerType}
          compLabels={compLabels}
          selectedCompLabel={selectedCompLabel}
          setSelectedBuyerType={setSelectedBuyerType}
          setSelectedCompLabel={setSelectedCompLabel}
          clientSecret={clientSecret}
          initialClientSecret={initialClientSecret}
          setCard={setCard}
          setStripe={setStripe}
          dueDateOpt={dueDateOpt}
          setDueDateOpt={setDueDateOpt}
          customDueDate={customDueDate}
          setCustomDueDate={setCustomDueDate}
          userType={userType}
          setUserType={setUserType}
          initialExistingUser={initialExistingUser}
          setInitialExistingUser={setInitialExistingUser}
          existingUser={existingUser}
          setExistingUser={setExistingUser}
          installmentMonths={installmentMonths}
          setInstallmentMonths={setInstallmentMonths}
          paymentPlans={paymentPlans}
          setPaymentDate={setPaymentDate}
          loadExistingUsers={loadExistingUsers}
          setIsCardValid={setIsCardValid}
          savedCards={savedCards}
          isLoadingCards={isLoadingCards}
          selectedCard={selectedCard}
          setSelectedCard={setSelectedCard}
          alert={alert}
          closeModal={closeModal}
          isValid={isValid}
          isSaving={isSaving}
          setIsSaving={setIsSaving}
          getText={getText}
          handleAssignToChange={handleAssignToChange}
          createOrder={createOrder}
          card={card}
          openLoading={openLoading}
          openError={openError}
          showSuccess={showSuccess}
          showPaymentSuccess={showPaymentSuccess}
          openMarkAsComplete={openMarkAsComplete}
          openDelete={openDelete}
          processTerminalPayment={processTerminalPayment}
          createPaymentPlanOrder={createPaymentPlanOrder}
          chargePaymentMethod={chargePaymentMethod}
          eventPackage={eventPackage}
          showAssignmentSuccess={showAssignmentSuccess}
          showRemoveSeat={Boolean(sourceOrderId)}
          onRemoveSeat={onRemoveSeat}
          organizationUUID={organizationUUIDForAssignment}
        />
      )}
    </Modal.Body>
  )
}
